import React from "react";
import classNames from "classnames";
import Slider from "react-slick";
import Styles from "./TrustPilotComponents.module.scss";
import SVG from "../SVG/SVG";
import { getTrustpilotData, getTrustpilotReviews } from "../../../api/trustpilot";
import "./TrustPilot.global.scss";
import { useQuery } from "react-query";

import SVG_trustpilotStar from "@/shared/assets/img_used/trustpilotStar.svg";
import SVG_trustpilotStarEmpty from "@/shared/assets/img_used/trustpilotStarEmpty.svg";
import SVG_trustpilotStarHalf from "@/shared/assets/img_used/trustpilotStarHalf.svg";
import { Icon } from "@/App/components/@remote";

type Props = {
  theme?: string;
  className?: string;
  header?: string;
  templateId:
    | "trustpilotDefault"
    | "trustpilotSimple"
    | "trustpilotSlider"
    | "trustpilotSliderSimple"
    | "trustpilotLPs"
    | "trustpilotLPsNewReviews"
    | "trustpilotLPsNewRating";
  width?: string;
  id?: string;
  hideCounter?: boolean;
};

const dateAsText = (date: string) => {
  const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];
  const _date = new Date(date);
  return `${_date.getDate()} ${monthNames[_date.getMonth()]}`;
};

const TrustPilotWidget = ({ className = "", theme = "light", templateId, hideCounter = false }: Props) => {
  const { data: trustpilotData } = useQuery(["trustpilotData"], getTrustpilotData);
  const { data: trustpilotReviews } = useQuery(["trustpilotReviews"], getTrustpilotReviews);
  console.log({templateId});
  
  return (
    <div className={Styles.anchorWrapper}>
      <a
        href="https://de.trustpilot.com/review/remind.me"
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block" }}
      >
        {templateId === "trustpilotDefault" && trustpilotData && (
          <TrustpilotDefault className={className} theme={theme} trustpilotData={trustpilotData} />
        )}
        {templateId === "trustpilotSimple" && trustpilotData && <TrustpilotSimple trustpilotData={trustpilotData} />}
        {templateId === "trustpilotSlider" && trustpilotData && (
          <TrustpilotReviewsSlider className={className} trustpilotReviews={trustpilotReviews} />
        )}
        {templateId === "trustpilotSliderSimple" && trustpilotData && (
          <TrustpilotSliderSimple theme={theme} className={className} trustpilotReviews={trustpilotReviews} />
        )}
        {templateId === "trustpilotLPs" && trustpilotData && (
          <TrustpilotLPs theme={theme} className={className} trustpilotData={trustpilotData} />
        )}
        {templateId === "trustpilotLPsNewReviews" && trustpilotReviews && (
          <>
            <NewLayoutReviewsSlider theme={theme} className={className} trustpilotReviews={trustpilotReviews} />
            <NewLayoutTrustpilotLPs theme={theme} className={className} trustpilotData={trustpilotData} />
          </>
        )}
        {templateId === "trustpilotLPsNewRating" && trustpilotData && (
          <>
            <NewLayoutTrustpilotLPs hideCounter={hideCounter} theme={theme} className={className} trustpilotData={trustpilotData} />
          </>
        )}
      </a>
    </div>
  );
};

function TrustpilotDefault({ trustpilotData, theme = "light", className }: TrustpilotDefaultProps) {
  const { score, numberOfReviews } = trustpilotData;
  const integerStars = Math.floor(score.stars); // Integer part of the stars
  const hasHalfStar = score.stars % 1 !== 0; // Check if there is a decimal part

  return (
    <div
      className={classNames(Styles.defaultMainWrapper, theme === "dark" && Styles.dark, className && className)}
      aria-label={"trustpilot-default"}
    >
      <div className={Styles.trustpilotWrapper}>
        <SVG name="trustpilotLogoWhiteText" className={Styles.trustpilotLogo} size="unset" />
        <div className={Styles.starsWrapper}>
          {[...Array(5)].map((star, i) => {
            const counter = i + 1;
            if (counter <= integerStars)
              return (
                <img
                  alt={`star${star}`}
                  key={`star${i}`}
                  src={SVG_trustpilotStar}
                  className={Styles.trustpilotBigStars}
                />
              );
            if (counter === integerStars + 1 && hasHalfStar)
              return (
                <img
                  alt={`star${star}`}
                  src={SVG_trustpilotStarHalf}
                  key={`star_${i}`}
                  className={Styles.trustpilotBigStars}
                />
              );
            return (
              <img
                alt={`star${star}`}
                src={SVG_trustpilotStarEmpty}
                key={`star_${i}`}
                className={Styles.trustpilotBigStars}
              />
            );
          })}
        </div>
        <p className={Styles.trustpilotInfos}>
          TrustScore <b>{score?.trustScore}</b> | <b>{numberOfReviews?.total}</b> Bewertungen
        </p>
      </div>
    </div>
  );
}

function TrustpilotReviewsSlider({ trustpilotReviews }: TrustpilotReviewsSliderProps) {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <div
      className={classNames(Styles.reviewsSliderWrapper, "trustPilotSliderWrapper")}
      aria-label={"trustpilot-slider"}
    >
      <hr />
      <p className={Styles.header}>Einige unserer 5-Sterne-Bewertungen</p>
      {trustpilotReviews?.reviews && (
        <Slider className="trustpilotSlider" {...settings}>
          {trustpilotReviews?.reviews?.map((review, i) => (
            <div key={`review_${i}`} className={Styles.reviewsSliderWrapper}>
              <p className={Styles.title}>{review.title}</p>
              <p className={Styles.text}>{review.text}</p>
              <span className={Styles.displayName}>{review.consumer.displayName}</span>
              <span className={Styles.date}>{dateAsText(review.createdAt)}</span>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
}

function TrustpilotSliderSimple({ className, trustpilotReviews }: TrustpilotSliderSimpleProps) {
  const settings = {
    dots: true,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 775,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <div
      className={classNames(Styles.simpleSliderWrapper, className, "trustPilotSliderSimpleWrapper")}
      aria-label={"trustpilot-slider-simple"}
    >
      {trustpilotReviews?.reviews && (
        <Slider className={"trustpilotSliderSimple"} {...settings}>
          {trustpilotReviews?.reviews?.map((review, i) => (
            <div key={`review_${i}`} className={Styles.reviewsSliderWrapper}>
              <div className={Styles.starsTextWrapper}>
                <SVG name="trustpilotStar5" className={Styles.fiveStars} size="unset" />
                <p className={Styles.smallText}>{dateAsText(review.createdAt)}</p>
              </div>
              <p className={Styles.simpleTitle}>{review.title}</p>
              <p className={Styles.simpleText}>{review.text}</p>
              <p className={Styles.smallText}>{review.consumer.displayName}</p>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
}

function TrustpilotSimple({ trustpilotData }: TrustpilotSimpleProps) {
  const starsText = stringRepresentationOfStars(trustpilotData?.score?.stars);
  return (
    <div className={Styles.simpleWidgetWrapper} aria-label={"trustpilot-simple"}>
      <p>{starsText && <b>{starsText}</b>}</p>
      <p className={Styles.simpleTrustscore}>
        <b> {trustpilotData?.score?.trustScore}</b> von 5{" "}
      </p>
      <SVG name="trustpilotLogoWhiteText" className={Styles.simpleLogo} size="unset" />
    </div>
  );
}

function TrustpilotLPs({ trustpilotData, theme }: TrustpilotLPSProps) {
  const starsText = stringRepresentationOfStars(trustpilotData?.score?.stars);
  const themeClass = theme === "dark" && Styles.dark;
  const { score } = trustpilotData;
  const integerStars = Math.floor(score.stars);
  const hasHalfStar = score.stars % 1 !== 0;

  return (
    <div className={classNames(themeClass, Styles.trustpilotLPs)} aria-label={"trustpilot-lps"}>
      <div>
        <span className={Styles.whatOurUsersSay}>Das Urteil unserer Kunden: </span>
        <span className={Styles.starsText}>{starsText && <b>{starsText}</b>}</span>
      </div>
      {/* <SVG name="trustpilotStar5" className={Styles.trustpilotLPsStars} /> */}
      <div className={Styles.starsWrapper}>
        {[...Array(5)].map((star, i) => {
          const counter = i + 1;
          if (counter <= integerStars)
            return (
              <img alt={`star${star}`} key={`star${i}`} src={SVG_trustpilotStar} className={Styles.trustpilotStars} />
            );
          if (counter === integerStars + 1 && hasHalfStar)
            return (
              <img
                alt={`star${star}`}
                src={SVG_trustpilotStarHalf}
                key={`star_${i}`}
                className={Styles.trustpilotStars}
              />
            );
          return (
            <img
              alt={`star${star}`}
              src={SVG_trustpilotStarEmpty}
              key={`star_${i}`}
              className={Styles.trustpilotStars}
            />
          );
        })}
      </div>
      <span className={Styles.starsRating}>
        <b>{trustpilotData?.score?.trustScore}</b> von 5, basierend auf {trustpilotData?.numberOfReviews?.total}
        <span> </span>Bewertungen
      </span>
      <SVG name="trustpilotLogoWhiteText" className={Styles.simpleLogo} />
    </div>
  );
}

function stringRepresentationOfStars(numOfStars: number) {
  const stars = {
    "0": "Ungenügend",
    "1": "Ungenügend",
    "1.5": "Ungenügend",
    "2": "Mangelhaft",
    "2.5": "Mangelhaft",
    "3": "Akzeptabel",
    "3.5": "Akzeptabel",
    "4": "gut",
    "4.5": "Hervorragend",
    "5": "Hervorragend",
  };

  if (numOfStars) {
    return Object.entries(stars).filter((values) => values[0] === numOfStars.toString())[0][1];
  } else {
    return "Hervorragend";
  }
}

function NewLayoutReviewsSlider({ className, trustpilotReviews }: TrustpilotSliderSimpleProps) {
  const settings = {
    dots: true,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 775,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    nextArrow: <Icon name="arrowRight" />,
    prevArrow: <Icon name="arrowLeft" />,
  };

  return (
    <div
      className={classNames(
        Styles.simpleSliderWrapper,
        className,
        "trustPilotSliderSimpleWrapper",
        "lp-new-tp-slider",
        Styles.lpNewTp
      )}
      aria-label={"trustpilot-slider-simple"}
    >
      {trustpilotReviews?.reviews && (
        <Slider className={"trustpilotSliderSimple"} {...settings}>
          {trustpilotReviews?.reviews?.map((review, i) => (
            <div key={`review_${i}`} className={Styles.reviewsSliderWrapper}>
              <div className={Styles.reviewBody}>
                <div className={Styles.starsTextWrapper}>
                  <SVG name="trustpilotStar5New" className={Styles.fiveStarsNew} size="unset" />
                </div>
                <p className={Styles.simpleTitle}>{review.title}</p>
                <p className={Styles.simpleText}>{review.text}</p>
              </div>
              <p className={Styles.smallText}>{review.consumer.displayName}</p>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
}

function NewLayoutTrustpilotLPs({ trustpilotData, theme, hideCounter = false }: TrustpilotLPSProps) {
  const starsText = stringRepresentationOfStars(trustpilotData?.score?.stars);
  const themeClass = theme === "dark" && Styles.dark;
  const { score } = trustpilotData;
  const integerStars = Math.floor(score.stars);
  const hasHalfStar = score.stars % 1 !== 0;

  return (
    <div className={classNames(themeClass, Styles.trustpilotLPs, Styles.lpNewTp)} aria-label={"trustpilot-lps"}>
      <div>
        <span className={Styles.starsText}>{starsText && <b>{starsText}</b>}</span>
      </div>
      <div className={Styles.starsWrapper}>
        {[...Array(5)].map((star, i) => {
          const counter = i + 1;
          if (counter <= integerStars)
            return (
              <img alt={`star${star}`} key={`star${i}`} src={SVG_trustpilotStar} className={Styles.trustpilotStars} />
            );
          if (counter === integerStars + 1 && hasHalfStar)
            return (
              <img
                alt={`star${star}`}
                src={SVG_trustpilotStarHalf}
                key={`star_${i}`}
                className={Styles.trustpilotStars}
              />
            );
          return (
            <img
              alt={`star${star}`}
              src={SVG_trustpilotStarEmpty}
              key={`star_${i}`}
              className={Styles.trustpilotStars}
            />
          );
        })}
      </div>
      {!hideCounter && (
        <span className={Styles.starsRating}>
          <strong>{trustpilotData?.numberOfReviews?.total}</strong> Bewertungen
        </span>
      )}
      <SVG name="trustpilotLogoWhiteText" className={Styles.simpleLogo} />
    </div>
  );
}

export { TrustPilotWidget };
